export const PROJECT_NAMESPACE = 'sentinel-ui';
export const SERVICE_NAMESPACE = `${PROJECT_NAMESPACE}/service`;
export const DEFAULT_DATE_FORMAT = 'YYYY/MM/DD HH:mm';
export const DEFAULT_DATE_FORMAT_TIMEZONE = 'YYYY/MM/DD HH:mm zz';
export const DEFAULT_DATE_TIMEZONE = 'America/Chicago';
export const DEFAULT_DATE_FORMAT_SANS_TIME = 'YYYY/MM/DD';
export const TIME_ONLY_DATE_FORMAT = 'h:mm:ss A';
export const ACCOUNTS_UI_SEARCH_QUERYPARAM_DATE_FORMAT = 'YYYY-MM-DD';
export const ActionState = Object.freeze({
  Open: 'OPEN',
  Approved: 'APPROVED',
  Rejected: 'REJECTED',
});

export const REJECT_REASONS = [
  'NSF',
  'RECENT_DEPOSIT_HOLD',
  'COMPLIANCE',
  'AML',
  'PER_CORRESPONDENT_REQUEST',
  'OUTSTANDING_ACAT',
  'INCORRECT_DISTRIBUTION_TYPE',
  'NAME_MISMATCH',
  'NO_INVALID_ACCOUNT',
  'NON_RESIDENCE_COUNTRY',
  'THIRD_PARTY',
  'THIRD_PARTY_INTERNATIONAL',
  'OTHER',
];

export const EvaluatedActionType = Object.freeze({
  AmountAvailable: 'AmountAvailable',
  ClearingAccountAge: 'ClearingAccountAge',
  DebitCardEnabled: 'DebitCardEnabled',
  DeceasedOwner: 'DeceasedOwner',
  IRADepartmentReview: 'IRADepartmentReview',
  PabPmOrJboAccount: 'PabPmOrJboAccount',
  RecentAchDeposit: 'RecentAchDeposit',
  RestrictionCode: 'RestrictionCode',
  OpenMarginCall: 'OpenMarginCall',
  Custom: 'Custom',
});

export const InstructionSource = Object.freeze({
  APEX_EMPLOYEE: 'Apex Employee',
  AUTO_SWEEPS: 'Auto Sweeps',
  WEB_SERVICE: 'Web Service',
  CORRESPONDENT_EMPLOYEE: 'Correspondent Employee',
  SENTINEL2: 'Sentinel 2',
  UNKNOWN: 'Unknown',
});

export const RetirementAccountType = Object.freeze({
  // RetirementAccountType type is called "accountRegistration" in /evaluated_transfers api responses.
  APEX_IRA: 'IRA Participant',
  IRA_SPOUSE: 'IRA Spouse',
  APEX_ROLLOVER_IRA: 'IRA Rollover',
  SEP_EMPLOYER_IRA: 'SEP Employer',
  APEX_SEP_IRA: 'SEP Participant',
  APEX_ROTH_IRA: 'Roth IRA',
  PLAN_TERMINATED_IRA: 'Plan terminated - IRA only',
  IRA_PARTICIPANT_MATURED: 'IRA Participant - Matured Plan',
  APEX_COVERDELL_EDUCATION_IRA: 'Education IRA',
  SBJCT_TO_DISABILITY_PYMT_IRA: 'Plan subject to Disability Payment',
  SBJCT_TO_DEATH_IRA: 'Plan subject to Death - IRA only',
  EMPLOYEE_IRA: 'Employee',
  EMPLOYEE_SPOUSE_IRA: 'Employee Spouse',
  INDIVIDUAL_401K: '401K',
  APEX_MONEY_PRCHSE_PENSION_PL: 'Money Purchase Plan',
  APEX_SIMPLE_IRA: 'Simple IRA',
  APEX_PROFIT_SHARING_PLAN: 'Profit Sharing Plan',
  TERMINATED_401K: 'Plan terminated - Non-IRA',
  PREMATURE_EXCEPTION_IRA: 'Premature Exception - IRA',
  SIMPLE_401K: 'Death - Non-IRA',
  APEX_BENEFICIARY_IRA: 'Beneficiary - IRA',
  DESIGNATED_ROTH_401K: 'Designated Roth 401(k)',
  ROTH_CONVERSION_IRA: 'Roth Conversion IRA',
});

export const IRADistributionReason = Object.freeze({
  NORMAL: 'Normal',
  DISABILITY: 'Disability',
  SOSEPP: 'SOSEPP',
  PREMATURE: 'Premature',
  DEATH: 'Death',
  EXCESS_CONTRIBUTION_REMOVAL_BEFORE_TAX_DEADLINE: 'Excess Contribution Removal Before Tax Deadline',
  EXCESS_CONTRIBUTION_REMOVAL_AFTER_TAX_DEADLINE: 'Excess Contribution Removal After Tax Deadline',
  ROLLOVER_TO_QUALIFIED_PLAN: 'Rollover To Qualified Plan',
  ROLLOVER_TO_IRA: 'Rollover To IRA',
  TRANSFER: 'Transfer',
  RECHARACTERIZATION_PRIOR_YEAR: 'Recharacterization Prior Year',
  RECHARACTERIZATION_CURRENT_YEAR: 'Recharacterization Current Year',
  CONVERSION: 'Conversion',
  MANAGEMENT_FEE: 'Management Fee',
  PREMATURE_SIMPLE_IRA_LESS_THAN_2_YEARS: 'Premature Simple IRA Less Than 2 Years',
  NORMAL_ROTH_IRA_GREATER_THAN_5_YEARS: 'Normal Roth IRA Greater Than 5 Years',
});

export const TransferType = Object.freeze({
  ACH: 'ACH',
  CASH_JOURNAL: 'Cash Journal',
  CHECK: 'Check',
  CREDIT: 'Credit',
  FEE: 'Fee',
  PAYPAL: 'PayPal',
  POSITION_JOURNAL: 'Position Journal',
  RTP: 'Real-Time Payment (RTP)',
  WIRE: 'Wire',
});

export const MarginCallType = Object.freeze({
  DB_MUTUAL_FUND: 'DB Mutual Fund',
  TRANSLATED_MUTUAL_FUND: 'Translated Mutual Fund',
  DB_FIXED_INCOME: 'DB Fixed Income',
  TRANSLATED_FIXED_INCOME: 'Translated Fixed Income',
  DB_FUTURES: 'DB Futures',
  TRANSLATED_FUTURES: 'Translated Futures',
  TOTAL_POSITION_TYPE: 'Total Position Type',
  CONCENTRATED_MAINTENANCE: 'Concentrated Maintenance',
  DAY_TRADING: 'Day Trading',
  EQUITY_MAINTENANCE: 'Equity Maintenance',
  GOOD_FAITH_VIOLATION: 'Good Faith Violation',
  LEVERAGE_MAINTENANCE: 'Leverage Maintenance',
  MONEY_DUE: 'Money Due',
  PORTFOLIO_MARGIN_MAINTENANCE: 'Portfolio Margin Maintenance',
  REQUIRED_MAINTENANCE: 'Required Maintenance',
  REG_T: 'Reg T',
  CASH_ACCOUNT_SHORTS: 'Cash Account Shorts',
});

export const TaxWithholdingType = Object.freeze({
  Fixed: 'FIXED',
  Percentage: 'PERCENTAGE',
});

export const REQUIRED_PERMISSIONS = ['sentinel.view'];

export const REQUIRED_EXTERNAL_PERMISSIONS = ['sentinel2.evaluatedTransfersFull.list'];

export const ROLES_PERMISSIONS = [
  /*
   If a user has a "Role" with respect to the sentinel api,
   they can do more things in the app,
   like edit amounts for transfers in ReviewPending.
   These are also referred to colloquially as "bang permissions".

   Api documentation on permissions:
   https://confluence.apexclearing.local/display/T2/Sentinel2+API+Permissions#Sentinel2APIPermissions-RolesPermissions
  */
  'sentinel.actions.resolve!dept.apex_marginanalyst',
  'sentinel.actions.resolve!dept.apex_marginmanager',
  'sentinel.actions.resolve!dept.apex_margindirector',
  'sentinel.actions.resolve!dept.apex_risk_mgmt',
  'sentinel.actions.resolve!dept.bpo_marginanalyst',
  'sentinel.actions.resolve!dept.bpo_iraanalyst',
  'sentinel.actions.resolve!dept.exec_mgmt',
  'sentinel.actions.resolve!dept.compliance',
  'sentinel.actions.resolve!dept.legal',
  'sentinel.actions.resolve!dept.acat',
  'sentinel.actions.resolve!dept.apex_new_accounts',
];

export const TRANSFER_STATES = [
  'ReviewPending',
  'Suspended',
  'SuspendedPendingReturn',
  'Approved',
  'FundsPosted',
  'PositionPosted',
  'SentToBank',
  'SentToRtpNetwork',
  'Completed',
  'PaymentScheduled',
  'RejectedFundsNotPosted',
  'RejectedPositionNotPosted',
  'PendingPrinting',
  'RejectedNotSentToBank',
  'RejectedUnapproved',
  'RejectedValidationFailed',
  'StoppedPayment',
  'SendToBankFailed',
  'FundsReturned',
  'ReturnedWaitingForReversal',
  'Postponed',
  'Returned',
  'Canceled',
  'FailedAtBank',
  'Voided',
  'FailedToSuspend',
  'PayPalReviewPending',
  'InvestorReviewPending',
];

export const TRANSFER_TYPES = Object.freeze({
  ach: 'ACH',
  journal: 'CASH_JOURNAL',
  wire: 'WIRE',
  check: 'CHECK',
  fee: 'FEE',
  credit: 'CREDIT',
  paypal: 'PAYPAL',
  positionJournal: 'POSITION_JOURNAL',
  rtp: 'RTP',
});

export const ACTION_ROLES = [
  'APEX_IRA_ANALYST',
  'APEX_MARGIN_ANALYST',
  'APEX_MARGIN_MANAGER',
  'APEX_MARGIN_DIRECTOR',
  'APEX_RISK_MANAGEMENT',
  'APEX_BANKING',
  'BPO_MARGIN_ANALYST',
  'BPO_IRA_ANALYST',
  'BPO_BANKING_ANALYST',
  'APEX_EXECUTIVE_MANAGEMENT',
  'APEX_COMPLIANCE',
  'APEX_LEGAL',
  'APEX_ACATS',
  'APEX_NEW_ACCOUNTS',
];

export const ALL_REQUESTED_PERMISSIONS = [
  ...REQUIRED_PERMISSIONS,
  ...ROLES_PERMISSIONS,
  ...REQUIRED_EXTERNAL_PERMISSIONS,
];

export const ROUTER_BASENAME = '/cashiering';



// WEBPACK FOOTER //
// ./src/common/constants.js